import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector } from '../hooks';

import { MandateActions } from '../actions/mandate/mandate.actions';
import { EmployeeActions } from '../actions/employee/employee.actions';

import type { Mandate } from '../interfaces/client/mandate.interface';
import type { Employee } from '../interfaces/employee/employee.interface';
import type { MandateVersion, MandateDocument } from '../reducers/mandate.reducer';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import StageChip from './components/StageChip';
import ClientMark from './components/ClientMark';
import Timeline, { TimelineStep } from './components/Timeline';
import KvRow from './components/KvRow';
import FileIcon from './components/FileIcon';

import MandateEditModal from './MandateEditModal';

import {
  formatZar,
  hash,
  deriveStage,
  deriveProgress,
  deriveLead,
  deriveCode,
  deriveId,
  initialsFromName,
  formatDate,
  daysBetween,
  fileExtension,
  formatBytes
} from './lib/mandate-helpers';

// =============================================================================
//  Mandate detail — v3 redesign
//
//  Backend gaps reflected in this view (clearly stubbed):
//    - Per-mandate progress %               → deterministic stub from id
//    - Lead partner + team roster           → deterministic stub from employees
//    - Timeline / milestones                → 7-step timeline synthesised from
//                                              signed / start_date / expected_end_date
//    - Counterparties                       → static placeholder list
//    - Recent documents                     → empty until /clients/:id/mandates/:id
//                                              data is loaded (different endpoint)
// =============================================================================

const TIMELINE_STEPS = [
  'Mandate signed',
  'Information memorandum',
  'Buyer outreach',
  'Indicative offers',
  'Due diligence',
  'Final agreement',
  'Closing'
] as const;

type TabKey = 'overview' | 'documents' | 'activity' | 'contacts' | 'workstreams' | 'fees';

const TABS: ReadonlyArray<{ key: TabKey; label: string; ready: boolean }> = [
  { key: 'overview', label: 'Overview', ready: true },
  // v3 — Active Storage attachments on the mandate.
  { key: 'documents', label: 'Documents', ready: true },
  // v3 — Activity is the paper_trail version history.  Backend
  // GET /mandates/:id/versions returns one entry per change (since
  // has_paper_trail was enabled — no historical backfill).
  { key: 'activity', label: 'Activity', ready: true },
  { key: 'contacts', label: 'Contacts', ready: false },
  { key: 'workstreams', label: 'Workstreams', ready: false },
  { key: 'fees', label: 'Fees', ready: false }
];

// Fields we never want to show in the activity feed — these change on
// every save and don't represent meaningful user intent.
const NOISY_FIELDS = new Set<string>(['updated_at']);

// Friendlier labels for common field names (so the user sees "Stage" not
// "stage", "Target close" not "expected_end_date").
const FIELD_LABEL: Record<string, string> = {
  stage: 'Stage',
  progress: 'Progress',
  lead_id: 'Lead',
  name: 'Name',
  client_id: 'Client',
  sector_id: 'Sector',
  signed: 'Signed',
  start_date: 'Start',
  expected_end_date: 'Target close',
  end_date: 'End date',
  fee: 'Retainer',
  transaction_size: 'Transaction size',
  transaction_fee: 'Transaction fee',
  transaction_type: 'Transaction type',
  status: 'Status'
};

const labelFor = (field: string): string =>
  FIELD_LABEL[field] || field.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** Format a `from`/`to` value for the activity feed. */
const formatVal = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') {
    // PaperTrail serialises Date / BigDecimal as their toString reps; if
    // we ever see a raw object, just JSON-stringify defensively.
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
};

/** Lightweight relative-time label (e.g. "2h ago", "Yesterday", "3 days ago"). */
const relativeFrom = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day} days ago`;
  if (day < 30) return `${Math.round(day / 7)}w ago`;
  return `${Math.round(day / 30)}mo ago`;
};

/** Pick the timeline step that should be "current" given progress %. */
const currentStepIndex = (progress: number): number => {
  if (progress >= 100) return TIMELINE_STEPS.length - 1;
  return Math.min(TIMELINE_STEPS.length - 1, Math.floor((progress / 100) * TIMELINE_STEPS.length));
};

const MandateDetailV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const mandates: Mandate[] = useAppSelector(({ mandate_store }) => mandate_store.mandates) || [];
  const employees: Employee[] =
    useAppSelector(({ employee_store }) => employee_store.employees) || [];
  // Version history keyed by mandate id — populated on Activity tab open.
  const versionsByMandate = useAppSelector(
    ({ mandate_store }) => mandate_store.versionsByMandate || {}
  );
  // Active Storage attachments keyed by mandate id — populated on detail
  // mount (so the Overview "Recent documents" card has data) and refreshed
  // when the user opens the Documents tab or uploads / removes a file.
  const documentsByMandate = useAppSelector(
    ({ mandate_store }) => mandate_store.documentsByMandate || {}
  );

  const [tab, setTab] = useState<TabKey>('overview');
  const [editing, setEditing] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    dispatch(MandateActions.loadMandates(noOp));
    dispatch(EmployeeActions.loadEmployees(noOp));
    if (id) {
      setDocsLoading(true);
      dispatch(MandateActions.loadMandateDocuments(id, () => setDocsLoading(false)));
    }
  }, [dispatch, id]);

  // Lazy-load version history when the user opens the Activity tab.  We
  // refetch every time the tab is opened so concurrent edits in another
  // window show up; the request is cheap (paper_trail is just a table read).
  useEffect(() => {
    if (tab !== 'activity' || !id) return;
    setVersionsLoading(true);
    dispatch(MandateActions.loadMandateVersions(id, () => setVersionsLoading(false)));
  }, [tab, id, dispatch]);

  // Refresh attachments when the user reopens the Documents tab so changes
  // made via another route (e.g. delete from the Overview card) are seen.
  useEffect(() => {
    if (tab !== 'documents' || !id) return;
    setDocsLoading(true);
    dispatch(MandateActions.loadMandateDocuments(id, () => setDocsLoading(false)));
  }, [tab, id, dispatch]);

  /**
   * Read the chosen file as a base64 data URL and dispatch the upload.
   * The backend's ImageHelper.create_attachment_for_image accepts the same
   * shape ("data:<mime>;base64,<payload>") for all binary uploads, so we
   * reuse it rather than introduce a multipart endpoint.
   */
  const handleUpload = (file: File) => {
    if (!id || uploading) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      if (!dataUrl) {
        setUploading(false);
        return;
      }
      setUploading(true);
      dispatch(
        MandateActions.attachMandateDocument(id, { name: file.name, data: dataUrl }, () =>
          setUploading(false)
        )
      );
    };
    reader.onerror = () => setUploading(false);
    reader.readAsDataURL(file);
  };

  const handleDelete = (attachmentId: number | string) => {
    if (!id) return;
    if (!window.confirm('Remove this attachment? This cannot be undone.')) return;
    dispatch(MandateActions.detachMandateDocument(id, attachmentId, noOp));
  };

  // Resolve mandate from id route param (string).
  const mandate = useMemo(() => mandates.find((m) => String(m.id) === String(id)), [mandates, id]);

  // The backend has no mandate↔personnel join, so the only team member we can
  // legitimately name is the recorded lead.  This used to hash-pick 3-5
  // colleagues and present them as the engagement team, the first carrying a
  // "Lead" chip — attaching real people to deals they had no part in.
  // Computed up here so the hook count stays stable regardless of the
  // loading / not-found early returns below (Rules of Hooks).
  const teamSlice = useMemo(() => {
    if (!mandate || !mandate.lead_id || employees.length === 0) return [] as Employee[];
    const lead = employees.find((e) => String(e.id) === String(mandate.lead_id));
    return lead ? [lead] : ([] as Employee[]);
  }, [mandate, employees]);

  // Loading / not found states ------------------------------------------------
  if (!mandate && mandates.length === 0) {
    return (
      <>
        <Topbar crumbs={['Deal Flow', 'Mandates', '…']} />
        <div className="page">
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Loading mandate…
          </div>
        </div>
      </>
    );
  }
  if (!mandate) {
    return (
      <>
        <Topbar crumbs={['Deal Flow', 'Mandates', 'Not found']} />
        <div className="page">
          <button className="back-link" type="button" onClick={() => navigate('/mandates')}>
            <Icon name="back" size={14} />
            All mandates
          </button>
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Mandate <strong>#{id}</strong> not found.
          </div>
        </div>
      </>
    );
  }

  // Derived values ------------------------------------------------------------
  const stage = deriveStage(mandate);
  const code = deriveCode(mandate);
  const formattedId = deriveId(mandate);
  // Real progress when present (backend `mandates.progress`); deterministic
  // 40..95% stub as fallback for rows that pre-date the v3 backfill.
  const progress = deriveProgress(mandate);

  const today = new Date();
  const daysActive = daysBetween(mandate.start_date || mandate.signed, today) ?? 0;
  const daysToTarget = daysBetween(today, mandate.expected_end_date) ?? 0;

  const leadEmployee = teamSlice[0];

  // Timeline ------------------------------------------------------------------
  const currentIdx = currentStepIndex(progress);
  const milestoneSteps: TimelineStep[] = TIMELINE_STEPS.map((label, i) => {
    let state: TimelineStep['state'] = 'pending';
    if (i < currentIdx) state = 'done';
    else if (i === currentIdx) state = 'current';
    // Pin some real dates to a few steps based on what we have
    let date: string | undefined;
    if (i === 0 && mandate.signed) date = formatDate(mandate.signed);
    else if (i === 1 && mandate.start_date) date = formatDate(mandate.start_date);
    else if (i === TIMELINE_STEPS.length - 1 && mandate.expected_end_date)
      date = formatDate(mandate.expected_end_date);
    return { label, state, date };
  });

  // --------------------------------------------------------------------------
  return (
    <>
      <Topbar
        crumbs={['Deal Flow', 'Mandates', code]}
        tools={
          <>
            <button className="btn ghost" type="button" onClick={() => navigate('/mandates')}>
              <Icon name="back" size={13} />
              All mandates
            </button>
            <button className="btn primary" type="button" onClick={() => setEditing(true)}>
              <Icon name="check" size={13} />
              Edit
            </button>
          </>
        }
      />
      {editing && <MandateEditModal mandate={mandate} onClose={() => setEditing(false)} />}
      <div className="page">
        {/* --- Hero card -------------------------------------------------- */}
        <div className="detail-hero">
          <div className="detail-hero-top">
            <ClientMark name={mandate.client} />
            <div className="meta">
              <div className="chips">
                <span className="chip muted mono">{formattedId}</span>
                <span className="chip muted">{mandate.transaction_type || 'Mandate'}</span>
                <StageChip stage={stage} />
              </div>
              <h1>{code}</h1>
              {/*
                Subtitle pieces — client name links to the client detail
                page (when client_id resolves), lead links to the lead's
                employee detail.  Plain text fallback when we can't
                resolve an id, so legacy rows still show the name.
              */}
              <div className="sub">
                {mandate.client && (
                  <>
                    {mandate.client_id ? (
                      <button
                        type="button"
                        className="back-link"
                        onClick={() => navigate(`/clients/${mandate.client_id}`)}
                        style={{ padding: 0, display: 'inline' }}
                      >
                        {mandate.client}
                      </button>
                    ) : (
                      <span>{mandate.client}</span>
                    )}
                  </>
                )}
                {mandate.sector && (
                  <>
                    {mandate.client && <span> · </span>}
                    <span>{mandate.sector}</span>
                  </>
                )}
                {(() => {
                  // Real lead name (from `mandates.lead_id` denormalised by jbuilder)
                  // takes precedence; otherwise fall back to the deterministic
                  // team-slice lead.  When we have a real lead_id, link to
                  // their employee detail page; the deterministic stub falls
                  // back to plain text.
                  const stubLead = leadEmployee
                    ? `${leadEmployee.first_name} ${leadEmployee.last_name}`
                    : undefined;
                  const lead = deriveLead(mandate, stubLead);
                  if (!lead) return null;
                  return (
                    <>
                      <span> · Lead: </span>
                      {mandate.lead_id ? (
                        <button
                          type="button"
                          className="back-link"
                          onClick={() => navigate(`/employees/${mandate.lead_id}`)}
                          style={{ padding: 0, display: 'inline' }}
                        >
                          {lead}
                        </button>
                      ) : (
                        <span>{lead}</span>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="value">
              <div className="eyebrow">Transaction value</div>
              <div className="v">{formatZar(mandate.transaction_size)}</div>
              <div className="sub">Target close · {formatDate(mandate.expected_end_date)}</div>
            </div>
          </div>

          <div className="detail-hero-stats">
            <div className="stat">
              <div className="label">Mandate progress</div>
              <div className="value">{progress}%</div>
            </div>
            <div className="stat">
              <div className="label">Days active</div>
              <div className="value">{daysActive > 0 ? daysActive : '—'}</div>
            </div>
            <div className="stat">
              <div className="label">Days to target</div>
              <div className="value">{daysToTarget > 0 ? daysToTarget : '—'}</div>
            </div>
            <div className="stat">
              <div className="label">Team members</div>
              <div className="value">{teamSlice.length}</div>
            </div>
          </div>
        </div>

        {/* --- Tabs ------------------------------------------------------- */}
        {/*
          All tabs are clickable.  Tabs whose backend wiring isn't done yet
          (ready: false) render a "Coming soon" pane on click — see the
          `tab !== 'overview'` block below.  A tiny dot before the label
          signals "this tab is a stub" to the user before they click.
        */}
        <div className="tab-strip">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`tab ${tab === t.key ? 'active' : ''} ${!t.ready ? 'stub' : ''}`}
              onClick={() => setTab(t.key)}
              title={t.ready ? undefined : 'Coming soon — backend wiring pending'}
            >
              {!t.ready && (
                <span aria-hidden style={{ opacity: 0.4 }}>
                  ·&nbsp;
                </span>
              )}
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="grid detail">
            {/* --- LEFT column -------------------------------------------- */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
              <div className="card flush">
                <div className="card-h">
                  <h3>Mandate timeline</h3>
                </div>
                <Timeline steps={milestoneSteps} />
              </div>

              <div className="card flush">
                <div className="card-h">
                  <h3>Recent documents</h3>
                  <div className="actions">
                    {/* Upload picks the file via a hidden <input> bound to a
                        label-styled-as-button.  handleUpload reads the file
                        as a base64 data URL and dispatches the attach saga. */}
                    <label
                      className={`btn ghost ${uploading ? 'disabled' : ''}`}
                      title={uploading ? 'Uploading…' : 'Upload a file to this mandate'}
                      style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}
                    >
                      <Icon name="upload" size={13} />
                      {uploading ? 'Uploading…' : 'Upload'}
                      <input
                        type="file"
                        hidden
                        disabled={uploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUpload(f);
                          // Reset so re-selecting the same file fires onChange.
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {(() => {
                      const docCount = (documentsByMandate[String(id)] || []).length;
                      return docCount > 5 ? (
                        <button
                          className="btn ghost"
                          type="button"
                          onClick={() => setTab('documents')}
                          title="View all attachments on this mandate"
                        >
                          View all ({docCount})
                          <Icon name="arrow" size={13} />
                        </button>
                      ) : null;
                    })()}
                  </div>
                </div>
                {(() => {
                  const docs = (documentsByMandate[String(id)] || []) as MandateDocument[];
                  if (docsLoading && docs.length === 0) {
                    return (
                      <div
                        className="muted"
                        style={{ padding: 36, textAlign: 'center', fontSize: 13.5 }}
                      >
                        Loading documents…
                      </div>
                    );
                  }
                  if (docs.length === 0) {
                    return (
                      <div
                        className="muted"
                        style={{ padding: 36, textAlign: 'center', fontSize: 13.5 }}
                      >
                        No documents attached yet. Upload one to start.
                      </div>
                    );
                  }
                  return (
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Filename</th>
                          <th className="right">Size</th>
                          <th>Uploaded</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docs.slice(0, 5).map((d) => (
                          <tr
                            key={d.id}
                            onClick={() => window.open(d.url, '_blank', 'noopener,noreferrer')}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <FileIcon filename={d.name} />
                            </td>
                            <td className="strong">{d.name}</td>
                            <td className="num right mono">{formatBytes(d.byte_size)}</td>
                            <td className="mono">{formatDate(d.created_at) || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>

            {/* --- RIGHT column ------------------------------------------- */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
              <div className="card">
                <h3
                  style={{
                    margin: '0 0 14px 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--ink)'
                  }}
                >
                  Engagement team
                </h3>
                {teamSlice.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13 }}>
                    No team members assigned.
                  </div>
                ) : (
                  teamSlice.map((e, i) => (
                    // Team rows navigate to the employee detail page when we
                    // have a real id.  Cursor hint + role=button makes the
                    // affordance discoverable; we keep the structural styling
                    // from .team-row.
                    <div
                      className="team-row"
                      key={e.id || i}
                      onClick={() => e.id && navigate(`/employees/${e.id}`)}
                      role={e.id ? 'button' : undefined}
                      tabIndex={e.id ? 0 : undefined}
                      onKeyDown={(ev) => {
                        if (e.id && (ev.key === 'Enter' || ev.key === ' ')) {
                          ev.preventDefault();
                          navigate(`/employees/${e.id}`);
                        }
                      }}
                      style={{ cursor: e.id ? 'pointer' : undefined }}
                    >
                      <div className="av">{initialsFromName(`${e.first_name} ${e.last_name}`)}</div>
                      <div className="who">
                        <span className="name">
                          {e.first_name} {e.last_name}
                          {i === 0 && <span className="chip gold">Lead</span>}
                        </span>
                        <span className="role">{e.role || 'Associate'}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="card">
                <h3
                  style={{
                    margin: '0 0 14px 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--ink)'
                  }}
                >
                  Key terms
                </h3>
                <div className="kv">
                  <KvRow label="Mandate type" value={mandate.transaction_type || '—'} />
                  <KvRow label="Sector" value={mandate.sector || '—'} />
                  <KvRow
                    label="Mandate fee"
                    value={formatZar(mandate.transaction_fee || mandate.fee)}
                    mono
                  />
                  <KvRow label="Retainer" value={formatZar(mandate.fee)} mono />
                  <KvRow label="Opened" value={formatDate(mandate.signed)} mono />
                  <KvRow label="Start" value={formatDate(mandate.start_date)} mono />
                  <KvRow label="Target close" value={formatDate(mandate.expected_end_date)} mono />
                  <KvRow
                    label="Confidentiality"
                    value={<span className="chip warn">Confidential</span>}
                  />
                </div>
              </div>

              <div className="card">
                <h3
                  style={{
                    margin: '0 0 14px 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--ink)'
                  }}
                >
                  Counterparties
                </h3>
                <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                  Backend has no counterparty model yet — placeholders below.
                </div>
                <div className="kv">
                  <KvRow label="Buyer" value="—" />
                  <KvRow label="Buyer lead" value="—" />
                  <KvRow label="Legal" value="—" />
                  <KvRow label="Reporting accountants" value="—" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- Documents tab (Active Storage attachments) --------------- */}
        {tab === 'documents' &&
          (() => {
            const docs = (documentsByMandate[String(id)] || []) as MandateDocument[];
            return (
              <div className="card flush">
                <div className="card-h">
                  <h3>Mandate documents</h3>
                  <div className="actions">
                    <label
                      className={`btn primary ${uploading ? 'disabled' : ''}`}
                      title={uploading ? 'Uploading…' : 'Upload a file to this mandate'}
                      style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}
                    >
                      <Icon name="upload" size={13} />
                      {uploading ? 'Uploading…' : 'Upload'}
                      <input
                        type="file"
                        hidden
                        disabled={uploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUpload(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
                {docsLoading && docs.length === 0 ? (
                  <div
                    className="muted"
                    style={{ padding: 48, textAlign: 'center', fontSize: 13.5 }}
                  >
                    Loading documents…
                  </div>
                ) : docs.length === 0 ? (
                  <div
                    className="muted"
                    style={{ padding: 48, textAlign: 'center', fontSize: 13.5 }}
                  >
                    No documents attached. Click <strong>Upload</strong> to add the first one.
                  </div>
                ) : (
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Filename</th>
                        <th className="right">Size</th>
                        <th>Uploaded</th>
                        <th className="actions-cell" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {docs.map((d) => (
                        <tr key={d.id}>
                          <td>
                            <FileIcon filename={d.name} />
                          </td>
                          <td className="strong">
                            <a
                              href={d.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'inherit' }}
                            >
                              {d.name}
                            </a>
                            <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>
                              {fileExtension(d.name) || 'BIN'}
                            </div>
                          </td>
                          <td className="num right mono">{formatBytes(d.byte_size)}</td>
                          <td className="mono">{formatDate(d.created_at) || '—'}</td>
                          <td className="actions-cell">
                            <button
                              type="button"
                              className="btn ghost"
                              title="Remove this attachment"
                              onClick={() => handleDelete(d.id)}
                              style={{ padding: '4px 8px', fontSize: 12 }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })()}

        {/* --- Activity tab (paper_trail version history) --------------- */}
        {tab === 'activity' &&
          (() => {
            const versions = (versionsByMandate[String(id)] || []) as MandateVersion[];
            // Filter out version entries whose only change is `updated_at`
            // (noise from saves that didn't touch any other field).
            const cleaned = versions
              .map((v) => ({
                ...v,
                changes: v.changes.filter((c) => !NOISY_FIELDS.has(c.field))
              }))
              .filter((v) => v.event !== 'update' || v.changes.length > 0);

            if (versionsLoading && cleaned.length === 0) {
              return (
                <div className="card" style={{ padding: 60, textAlign: 'center' }}>
                  <span className="muted">Loading activity…</span>
                </div>
              );
            }
            if (cleaned.length === 0) {
              return (
                <div className="card" style={{ padding: 60, textAlign: 'center' }}>
                  <div className="eyebrow" style={{ marginBottom: 10 }}>
                    No activity yet
                  </div>
                  <div
                    style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--ink)' }}
                  >
                    This mandate hasn't been edited since version tracking was enabled.
                  </div>
                  <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
                    Future field changes (stage, progress, lead, etc.) will appear here.
                  </div>
                </div>
              );
            }

            return (
              <div className="card flush">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: '22%' }}>When</th>
                      <th style={{ width: '18%' }}>Actor</th>
                      <th>Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleaned.map((v) => (
                      <tr key={v.id}>
                        <td>
                          <div className="mono" style={{ fontSize: 12 }}>
                            {formatDate(v.created_at)}
                          </div>
                          <div className="muted" style={{ fontSize: 11.5 }}>
                            {relativeFrom(v.created_at)}
                          </div>
                        </td>
                        <td>
                          {v.actor ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  background: 'var(--ink)',
                                  color: 'var(--gold-soft)',
                                  display: 'inline-grid',
                                  placeItems: 'center',
                                  fontSize: 10,
                                  fontWeight: 600
                                }}
                              >
                                {initialsFromName(v.actor)}
                              </span>
                              <span>{v.actor}</span>
                            </div>
                          ) : (
                            <span className="muted">System</span>
                          )}
                        </td>
                        <td>
                          {v.event === 'create' ? (
                            <span>
                              <span className="chip gold">Created</span> the mandate.
                            </span>
                          ) : v.event === 'destroy' ? (
                            <span>
                              <span className="chip warn">Deleted</span> the mandate.
                            </span>
                          ) : (
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5 }}>
                              {v.changes.map((c, i) => (
                                <li key={`${v.id}-${c.field}-${i}`}>
                                  <strong>{labelFor(c.field)}</strong> changed from{' '}
                                  <span className="muted">{formatVal(c.from)}</span> to{' '}
                                  <span style={{ color: 'var(--ink)' }}>{formatVal(c.to)}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

        {/* --- Other stubbed tabs --------------------------------------- */}
        {tab !== 'overview' && tab !== 'activity' && tab !== 'documents' && (
          <div className="card" style={{ padding: 60, textAlign: 'center' }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              Coming soon
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)' }}>
              {TABS.find((t) => t.key === tab)?.label} tab
            </div>
            <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
              Stubbed in the v3 pilot — wire to a backend feature when ready.
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MandateDetailV3;
